import { Platform } from 'react-native';
import type { DeliveryAddress } from '../types/delivery';
import {
  deliveryAddressFromProfile,
  isCompleteDeliveryAddress,
  profileAddressObjectFromDelivery,
} from '../utils/deliveryAddressFromProfile';

const LOCAL_KEY = 'bookapt_saved_delivery_address';

export type SavedDeliveryAddressSource = 'profile' | 'local' | 'none';

export async function loadSavedDeliveryAddress(input: {
  userId?: string;
  userAddress?: unknown;
  fallbackPhone?: string;
  guestEmail?: string;
}): Promise<{ address: DeliveryAddress; source: SavedDeliveryAddressSource } | null> {
  const fromProfile = deliveryAddressFromProfile({
    userAddress: input.userAddress as Parameters<typeof deliveryAddressFromProfile>[0]['userAddress'],
    fallbackPhone: input.fallbackPhone,
  });
  if (fromProfile) {
    return { address: fromProfile, source: 'profile' };
  }

  if (Platform.OS === 'web' && typeof localStorage !== 'undefined') {
    const keys = [
      input.guestEmail?.trim()
        ? `${LOCAL_KEY}_${input.guestEmail.trim().toLowerCase()}`
        : null,
      input.userId ? `${LOCAL_KEY}_${input.userId}` : null,
      LOCAL_KEY,
    ].filter(Boolean) as string[];

    for (const key of keys) {
      try {
        const raw = localStorage.getItem(key);
        if (!raw) continue;
        const parsed = JSON.parse(raw) as DeliveryAddress;
        if (isCompleteDeliveryAddress(parsed)) {
          return {
            address: {
              ...parsed,
              phone: parsed.phone || input.fallbackPhone?.trim() || undefined,
            },
            source: 'local',
          };
        }
      } catch {
        /* ignore corrupt cache */
      }
    }
  }

  return null;
}

export async function saveDeliveryAddressForFuture(input: {
  userId?: string;
  guestEmail?: string;
  address: DeliveryAddress;
}): Promise<void> {
  const { address, userId, guestEmail } = input;
  if (!isCompleteDeliveryAddress(address)) return;

  if (Platform.OS === 'web' && typeof localStorage !== 'undefined') {
    const payload = JSON.stringify(address);
    localStorage.setItem(LOCAL_KEY, payload);
    if (guestEmail?.trim()) {
      localStorage.setItem(`${LOCAL_KEY}_${guestEmail.trim().toLowerCase()}`, payload);
    }
    if (userId) {
      localStorage.setItem(`${LOCAL_KEY}_${userId}`, payload);
    }
  }

  if (!userId) return;

  const addressObject = profileAddressObjectFromDelivery(address);
  const { UnifiedAuthService } = await import('./unifiedAuthService');
  const user = await UnifiedAuthService.getCurrentUser();
  if (!user?.id || user.id !== userId) return;

  const updatedUser = {
    ...user,
    address: addressObject,
    phone: user.phone || address.phone || '',
  };

  if (Platform.OS === 'web') {
    const { doc, updateDoc } = require('firebase/firestore');
    const { getWebDb, initializeFirebase } = require('./unifiedAuthService');
    if (!initializeFirebase()) return;
    const db = getWebDb();
    if (!db) return;
    await updateDoc(doc(db, 'users', userId), {
      address: addressObject,
      ...(address.phone ? { phone: address.phone } : {}),
    });
    try {
      const ProductionStorage = (await import('../utils/productionStorage')).default;
      await ProductionStorage.setItem(
        'user',
        JSON.stringify({ ...updatedUser, lastLogin: new Date().toISOString() })
      );
    } catch {
      /* ignore cache sync */
    }
  } else {
    const { AuthManager } = await import('../utils/authManager');
    await AuthManager.saveUser(updatedUser as any);
  }
}
